/**
 * O que faz: lista os follow-ups que as regras impediram de sair, com o motivo de cada um.
 * Onde usa: FollowupCampaignMetrics, ao clicar em "Bloqueados pelas regras".
 * Conexões: Dialog compartilhado e dados de useFollowupBloqueados.
 * Tabelas: nenhuma direta; recebe as linhas já carregadas de follow_up_execucoes.
 * Data: 06/10/2026 (cores do Gens Design System em 07/10/2026)
 */

import { useMemo, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Phone, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import {
  ROTULO_MOTIVO_BLOQUEIO,
  type FollowupBloqueado,
  type MotivoBloqueioFollowup,
} from '@/features/followup/blockedExecutions';
import { cn } from '@/lib/utils';

interface FollowupBlockedExecutionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bloqueados: FollowupBloqueado[];
  isLoading: boolean;
}

type FiltroMotivo = MotivoBloqueioFollowup | 'todos';

export const FollowupBlockedExecutionsDialog = ({
  open,
  onOpenChange,
  bloqueados,
  isLoading,
}: FollowupBlockedExecutionsDialogProps) => {
  const [filtro, setFiltro] = useState<FiltroMotivo>('todos');

  // Conta por motivo para mostrar só os filtros que têm itens no período.
  const contagemPorMotivo = useMemo(() => {
    const contagem = new Map<MotivoBloqueioFollowup, number>();
    for (const item of bloqueados) contagem.set(item.motivo, (contagem.get(item.motivo) ?? 0) + 1);
    return contagem;
  }, [bloqueados]);

  const visiveis = filtro === 'todos' ? bloqueados : bloqueados.filter((item) => item.motivo === filtro);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gens-escopo flex max-h-[88vh] w-[94vw] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:rounded-3xl">
        <DialogHeader className="border-b bg-card px-4 py-4 sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-left text-base text-foreground">
            <ShieldCheck className="h-4 w-4 text-accent-foreground" />
            Follow-ups bloqueados pelas regras
          </DialogTitle>
          <DialogDescription className="text-left text-xs text-muted-foreground">
            Mensagens que não saíram para proteger o número e o contexto da conversa.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5 border-b px-4 py-3 sm:px-6">
          <BotaoFiltro ativo={filtro === 'todos'} onClick={() => setFiltro('todos')}>
            Todos ({bloqueados.length})
          </BotaoFiltro>
          {Array.from(contagemPorMotivo.entries()).map(([motivo, quantidade]) => (
            <BotaoFiltro key={motivo} ativo={filtro === motivo} onClick={() => setFiltro(motivo)}>
              {ROTULO_MOTIVO_BLOQUEIO[motivo]} ({quantidade})
            </BotaoFiltro>
          ))}
        </div>

        {/* Fade nas bordas (crm-scroll-fade, padrão do AGENTS.md) indica que há mais itens ao rolar. */}
        <div className="crm-scroll crm-scroll-fade min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <ThinkingOrbLoader />
            </div>
          ) : visiveis.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              Nenhum follow-up foi bloqueado neste período.
            </p>
          ) : (
            <ul className="divide-y">
              {visiveis.map((item) => (
                <li key={item.id} className="px-4 py-3 sm:px-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground">{item.nome?.trim() || 'Lead sem nome'}</p>
                    <span className="text-xs text-muted-foreground">{format(new Date(item.bloqueadoEm), 'dd/MM/yyyy HH:mm')}</span>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {item.telefone || 'Telefone não informado'} · {item.campaignName}
                  </p>
                  <p className="mt-1 text-xs">
                    <span className="rounded-md bg-warning-subtle px-1.5 py-0.5 font-medium text-warning-text">
                      {ROTULO_MOTIVO_BLOQUEIO[item.motivo]}
                    </span>
                    <span className="ml-2 text-muted-foreground">{item.detalhe}</span>
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface BotaoFiltroProps {
  ativo: boolean;
  onClick: () => void;
  children: ReactNode;
}

const BotaoFiltro = ({ ativo, onClick, children }: BotaoFiltroProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={ativo}
    className={cn(
      'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
      ativo ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground hover:bg-accent',
    )}
  >
    {children}
  </button>
);
